import {
  CreateProjectFromTemplateBody,
  ProjectTemplate,
  ProjectTemplateCreateBody,
  ProjectTemplateVersionCreateBody,
} from '@/types/project-templates'
import useCreateDetail, {
  UseCreateDetailOptions,
} from '../core-hooks/useCreateDetail'
import { createDetail } from '../request-wrappers'
import buildService from '../create-service'
import useGetDetail, { UseGetDetailOptions } from '../core-hooks/useGetDetail'
import useGetList, { UseGetListOptions } from '../core-hooks/useGetList'
import queryKeys from '../query-keys'

const service = buildService<ProjectTemplate>({
  pathCategory: 'project_templates',
})

export function useGetProjectTemplate({
  id,
  ...options
}: UseGetDetailOptions<ProjectTemplate>) {
  const query = useGetDetail<ProjectTemplate>({
    id,
    queryKey: queryKeys.projectTemplates.detail({
      id,
      filters: options.filters,
    }),
    serviceFn: service.getDetail,
    ...options,
  })

  return {
    projectTemplate: query.data,
    ...query,
  }
}

export function useGetProjectTemplates({
  filters,
  ...options
}: UseGetListOptions<ProjectTemplate> = {}) {
  const query = useGetList<ProjectTemplate>({
    filters,
    queryKey: queryKeys.projectTemplates.list({ filters }),
    serviceFn: service.getList,
    ...options,
  })

  return {
    projectTemplates: query.allData,
    ...query,
  }
}

/**
 * MARK: Action Hook: Create Project Template
 * Creates a new project template.
 */
export function useCreateProjectTemplate(
  options: UseCreateDetailOptions<ProjectTemplateCreateBody> = {},
) {
  const mutation = useCreateDetail<ProjectTemplateCreateBody>({
    serviceFn: (serviceFnOptions) => {
      return createDetail({
        url: '/v2/template/projects/create',
        ...serviceFnOptions,
      })
    },
    ...options,
  })

  return {
    createProjectTemplate: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Create Project Template Version
 * Creates a new project template version.
 */
export function useCreateProjectTemplateVersion(
  options: UseCreateDetailOptions<ProjectTemplateVersionCreateBody> = {},
) {
  const mutation = useCreateDetail<ProjectTemplateVersionCreateBody>({
    serviceFn: (serviceFnOptions) => {
      return createDetail({
        url: '/v2/template/projects/create-version',
        ...serviceFnOptions,
      })
    },
    ...options,
  })

  return {
    createProjectTemplateVersion: mutation.mutateAsync,
    ...mutation,
  }
}

/**
 * MARK: Action Hook: Create Project From Template
 * Creates a new project from a template.
 */
export function useCreateProjectFromTemplate(
  options: UseCreateDetailOptions<
    CreateProjectFromTemplateBody,
    { id: string }
  > = {},
) {
  const mutation = useCreateDetail<
    CreateProjectFromTemplateBody,
    { id: string }
  >({
    serviceFn: (serviceFnOptions) => {
      return createDetail({
        url: '/v2/template/projects/create-from-template',
        ...serviceFnOptions,
      })
    },
    ...options,
  })

  return {
    createProjectFromTemplate: mutation.mutateAsync,
    ...mutation,
  }
}
